import { z } from 'zod';

const id = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u);
export const hqAnnualPolicyYearSchema = z.object({ year: z.number().int().min(2000).max(2200) }).strict();
export const hqAnnualPolicyDraftSchema = z.object({
  year: z.number().int().min(2000).max(2200),
  vision: z.string().trim().min(1).max(2000),
  goal: z.string().trim().min(1).max(2000),
  effective: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u),
  targets: z.array(z.object({
    siteId: id,
    targetName: z.string().trim().min(1).max(200).optional(),
    item: z.string().trim().min(1).max(2000),
    value: z.number().finite().nonnegative().max(1e9),
    unit: z.string().trim().min(1).max(30),
  }).strict()).max(200),
  principles: z.array(z.string().trim().min(1).max(10000)).min(1).max(50),
}).strict().superRefine((draft, ctx) => {
  for (const [index,target] of draft.targets.entries()) {
    if (target.siteId === 'custom-target' ? !target.targetName : target.targetName !== undefined)
      ctx.addIssue({ code: 'custom', path: ['targets',index,'targetName'], message: 'A custom target name is required only for a custom target' });
  }
  if (draft.effective.slice(0, 4) !== String(draft.year) ||
      !Number.isFinite(Date.parse(`${draft.effective}T00:00:00Z`)) ||
      new Date(`${draft.effective}T00:00:00Z`).toISOString().slice(0, 10) !== draft.effective)
    ctx.addIssue({ code: 'custom', message: 'A valid effective date within the management year is required' });
});
export type HqAnnualPolicyDraft = z.infer<typeof hqAnnualPolicyDraftSchema>;
export const hqAnnualPolicyDocumentSchema = z.object({
  schemaVersion: z.literal(1), tenantId: z.literal('kop'),
  year: z.number().int().min(2000).max(2200), version: z.number().int().positive(),
  draft: hqAnnualPolicyDraftSchema,
  sites: z.array(z.object({ id, name: z.string().min(1).max(200) }).strict()).max(201),
  savedAt: z.string().datetime(), savedBy: id,
  contentHash: z.string().regex(/^[a-f0-9]{64}$/u),
}).strict();
export type HqAnnualPolicyDocument = z.infer<typeof hqAnnualPolicyDocumentSchema>;
export const hqAnnualPolicyLoadResultSchema = z.object({
  document: hqAnnualPolicyDocumentSchema.nullable(),
  sites: z.array(z.object({ id, name: z.string().min(1).max(200) }).strict()).max(201),
  canEdit: z.boolean(),
}).strict();
export type HqAnnualPolicyLoadResult = z.infer<typeof hqAnnualPolicyLoadResultSchema>;
export const hqAnnualPolicySaveInputSchema = z.object({
  commandId: id, expectedVersion: z.number().int().nonnegative(), draft: hqAnnualPolicyDraftSchema,
}).strict();
export const hqAnnualPolicySaveResultSchema = z.object({
  document: hqAnnualPolicyDocumentSchema,
  receipt: z.object({ commandId: id, version: z.number().int().positive(), savedAt: z.string().datetime(), replayed: z.boolean() }).strict(),
}).strict();
export type HqAnnualPolicySaveResult = z.infer<typeof hqAnnualPolicySaveResultSchema>;
